import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {Spinner} from '@primer/react'
import type React from 'react'
import {useCallback} from 'react'

import {useDragDrop} from '../hooks/use-drag-drop'
import {DragAndDropSVG} from './DragAndDropSVG'
import styles from './FullScreenDragOverlay.module.css'

type FullScreenDragOverlayProps = {
  onFilesDrop: (files: FileList) => void
  isUploading?: boolean
}

/**
 * A generic fullscreen drag-and-drop overlay component that provides visual feedback
 * during file drag operations and handles file upload workflows.
 *
 * This component displays a fullscreen overlay when files are being dragged over the page,
 * showing appropriate UI states for drag feedback and upload progress.
 *
 * @param onFilesDrop - Callback fired when files are dropped.
 * @param isUploading - Whether files are currently being uploaded (shows upload progress UI).
 *
 * @example
 * ```tsx
 * <FullScreenDragOverlay
 *   onFilesDrop={handleUpload}
 *   isUploading={isUploading}
 * />
 * ```
 */
export function FullScreenDragOverlay({
  onFilesDrop,
  isUploading = false,
}: FullScreenDragOverlayProps): React.ReactElement | null {
  const handleFilesDrop = useCallback(
    (files: FileList) => {
      // Prevent drops during upload
      if (isUploading) return
      onFilesDrop(files)
    },
    [onFilesDrop, isUploading],
  )

  const {isDragging} = useDragDrop({
    onFilesDrop: handleFilesDrop,
    targetElement: ssrSafeDocument,
  })

  let content: React.ReactNode = null

  if (isUploading) {
    content = (
      <>
        <Spinner size="large" />
        <p className={styles.text}>Uploading files...</p>
      </>
    )
  } else if (isDragging) {
    content = (
      <>
        <DragAndDropSVG />
        <p className={styles.text}>Drop files to upload</p>
      </>
    )
  }

  if (!content) return null

  return (
    <div className={styles.overlay}>
      <div className={styles.centeredContent}>{content}</div>
    </div>
  )
}
